import { RootState } from '@/app/redux/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// State for confirmation dialogue and confirmation callback
interface ConfirmationDialogueState {
  title: string;
  description?: string | string[];
  checkboxOption?: {
    label: string;
    default?: boolean;
  };
  variant?: 'default' | 'destructive';
}
let confirmationDialogueCallback:
  | ((optionSelected: boolean) => Promise<void>)
  | null = null;
export const setConfirmationDialogueCallback = (
  callback: (confirmation: boolean) => Promise<void>,
) => {
  confirmationDialogueCallback = callback;
};
export const getAndClearConfirmationDialogueCallback = () => {
  const callback = confirmationDialogueCallback;
  confirmationDialogueCallback = null;
  return callback;
};

// State for student creation modal callback
let studentCreationCallback: ((studentId?: string) => void) | null = null;
export const setStudentCreationCallback = (
  callback: (studentId?: string) => void,
) => {
  studentCreationCallback = callback;
};
export const getAndClearStudentCreationCallback = () => {
  const callback = studentCreationCallback;
  studentCreationCallback = null;
  return callback;
};

interface ModalsState {
  renderCount: number;
  overSeatLimitModalOpen: boolean;
  contactFormModalOpen: boolean;
  confirmationDialogue: ConfirmationDialogueState | null;
  attemptedProFeature: string | null;
  trialEndedModalOpen: boolean;
  studentCreationModalOpen: boolean;
}

const initialState: ModalsState = {
  renderCount: 0,
  overSeatLimitModalOpen: false,
  contactFormModalOpen: false,
  confirmationDialogue: null,
  attemptedProFeature: null,
  trialEndedModalOpen: false,
  studentCreationModalOpen: false,
};

const modalsSlice = createSlice({
  name: 'modals',
  initialState,
  reducers: {
    refreshModals: (state) => {
      state.renderCount++;
    },
    setOverSeatLimitModalOpen: (state, action: PayloadAction<boolean>) => {
      state.overSeatLimitModalOpen = action.payload;
    },
    setContactFormModalOpen: (state, action: PayloadAction<boolean>) => {
      state.contactFormModalOpen = action.payload;
    },
    openConfirmationDialogue: (
      state,
      action: PayloadAction<ConfirmationDialogueState>,
    ) => {
      state.confirmationDialogue = action.payload;
      state.renderCount++;
    },
    closeConfirmationDialogue: (state) => {
      state.confirmationDialogue = null;
      state.renderCount++;
    },
    setAttemptedProFeature: (state, action: PayloadAction<string | null>) => {
      state.attemptedProFeature = action.payload;
    },
    setTrialEndedModalOpen: (state, action: PayloadAction<boolean>) => {
      state.trialEndedModalOpen = action.payload;
    },
    setStudentCreationModalOpen: (state, action: PayloadAction<boolean>) => {
      state.studentCreationModalOpen = action.payload;
    },
  },
});

export const {
  refreshModals,
  setOverSeatLimitModalOpen,
  setContactFormModalOpen,
  openConfirmationDialogue,
  closeConfirmationDialogue,
  setAttemptedProFeature,
  setTrialEndedModalOpen,
  setStudentCreationModalOpen,
} = modalsSlice.actions;

export default modalsSlice.reducer;

// Selectors
export const selectModals = (state: RootState) => state.modals;
